/**
 * App Top Bar
 * 
 * Shared top bar component used across all pages.
 * Extracted from Research Lab's IDEShell pattern.
 * 
 * Features:
 * - Quanthop logo (left)
 * - Breadcrumb navigation (left, after logo)
 * - Center content slot (search, filters, etc.)
 * - Right actions slot (buttons)
 * - Jobs indicator (global, shows active jobs)
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useDarkMode } from '../../hooks/useDarkMode';
import { JobsIndicator } from '../jobs/JobsIndicator';
import { useJobsStore } from '../../store/jobsStore';
import { useCredits } from '../../hooks/useCredits';

interface AppTopBarProps {
    /** Breadcrumb elements to show after the logo */
    breadcrumbs?: React.ReactNode;
    /** Center content (search, filters, etc.) */
    centerContent?: React.ReactNode;
    /** Right side actions (buttons, etc.) */
    rightActions?: React.ReactNode;
}

export const AppTopBar: React.FC<AppTopBarProps> = ({
    breadcrumbs,
    centerContent,
    rightActions,
}) => {
    const navigate = useNavigate();
    const { dark } = useDarkMode();
    const openJobsSidebar = useJobsStore((state) => state.openJobsSidebar);
    const { credits } = useCredits();
    const [creditsOpen, setCreditsOpen] = useState(false);
    const creditsRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (creditsRef.current && !creditsRef.current.contains(e.target as Node)) {
                setCreditsOpen(false);
            }
        };
        if (creditsOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [creditsOpen]);

    const usagePercent = credits
        ? Math.min(100, Math.round((credits.creditsUsedThisMonth / credits.creditsPerMonth) * 100))
        : 0;
    const balancePercent = credits
        ? Math.min(100, Math.round((credits.creditBalance / credits.creditsPerMonth) * 100))
        : 0;

    return (
        <div className="flex items-center px-3 py-2 bg-white dark:bg-[#181825] border-b border-gray-200 dark:border-white/[0.06] select-none flex-shrink-0">
            {/* Left: Logo + Breadcrumbs - flex-1 to balance with right */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Quanthop Logo */}
                <button
                    onClick={() => navigate('/app')}
                    className="flex items-center hover:opacity-80 transition-opacity"
                >
                    <img
                        src={dark ? "/quanthop_logo_dark.svg" : "/quanthop_logo_light.svg"}
                        alt="Quanthop"
                        className="h-6 w-auto"
                    />
                </button>

                {/* Breadcrumbs */}
                {breadcrumbs && (
                    <>
                        <div className="h-5 w-px bg-gray-300 dark:bg-gray-700" />
                        {breadcrumbs}
                    </>
                )}
            </div>

            {/* Center: Custom content - stays planted */}
            {centerContent && (
                <div className="flex items-center gap-3 flex-shrink-0">
                    {centerContent}
                </div>
            )}

            {/* Right: Actions - flex-1 to balance with left, justify-end for alignment */}
            <div className="flex items-center gap-2 flex-1 justify-end">
                {/* Credit Balance Dropdown */}
                {credits && (
                    <div className="relative" ref={creditsRef}>
                        <button
                            onClick={() => setCreditsOpen(o => !o)}
                            className="flex items-center gap-1.5 px-2 py-1 rounded text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                            title={`${credits.creditBalance} / ${credits.creditsPerMonth} credits this month`}
                        >
                            <FontAwesomeIcon icon={faCoins} className="w-3.5 h-3.5 text-gray-700 dark:text-white" />
                            <span>{credits.creditBalance.toLocaleString()}</span>
                        </button>

                        {creditsOpen && (
                            <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#1e1e2e] border border-gray-200 dark:border-white/[0.08] rounded-lg shadow-xl z-50 p-4">
                                {/* Balance */}
                                <div className="mb-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">Balance</span>
                                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                                            {credits.creditBalance.toLocaleString()}
                                            <span className="text-gray-500"> / {credits.creditsPerMonth.toLocaleString()}</span>
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                        <div
                                            className="bg-amber-500 h-1.5 rounded-full transition-all"
                                            style={{ width: `${balancePercent}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Used This Month */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">Used This Month</span>
                                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                                            {credits.creditsUsedThisMonth.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                        <div
                                            className="bg-purple-500 h-1.5 rounded-full transition-all"
                                            style={{ width: `${usagePercent}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Billing Page Link */}
                                <Link
                                    to="/app/billing"
                                    onClick={() => setCreditsOpen(false)}
                                    className="flex items-center justify-center gap-2 w-full px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/[0.05] hover:bg-gray-200 dark:hover:bg-white/[0.1] rounded-md transition-colors"
                                >
                                    Manage Credits
                                    <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}
                {/* Jobs Indicator - global, shows when jobs are active */}
                <JobsIndicator onClick={openJobsSidebar} />
                {rightActions}
            </div>
        </div>
    );
};

export default AppTopBar;
